import { useEffect, useState } from "react";
import api from "@/lib/api";
import axios from "axios";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";
import { LEGACY_PIPELINE, getLegacyPipelineIdx } from "@/components/StatusPipeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "—";
const fmtDT   = d => d ? new Date(d).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CHARGER_ICONS = { "Small Charger":"🔋", "Fast Charger":"⚡", "Rickshaw Charger":"🛺" };
const STATUS_DOT = {
  "Pick Up Request":"#7c3aed","Pick Up Accepted":"#4f46e5","Barcode Generated":"#0891b2",
  "Picked Up":"#0284c7","In Transit":"#1d4ed8","Received":"#475569",
  "Repairing":"#d97706","In Dispatch":"#ea580c",
};
const DEFAULT_DOT = "#16a34a";
const WARRANTY_BADGE = {
  "Under Warranty":  { bg:"#f0fdf4", text:"#15803d", border:"#86efac" },
  "Out of Warranty": { bg:"#fff7ed", text:"#c2410c", border:"#fed7aa" },
  "Expired":         { bg:"#fff1f2", text:"#be123c", border:"#fda4af" },
  "XP Charger":      { bg:"#eff6ff", text:"#1d4ed8", border:"#bfdbfe" },
};

// ── Single SC Job Card (inline, for bulk print) ──────────────────────────────
function SCJobCard({ job, sc }) {
  const companyName = sc?.name    || "XSTRONG POWER EV CHARGER";
  const tagline     = sc?.tagline || "EV Charger Repair & Service Center";
  const trackingUrl = `${window.location.origin}/sc-track?job=${job.sc_no}`;
  const chargers    = job.chargers || [];
  const specs       = job.specs   || {};
  const cust        = job.customer || {};
  const courier     = job.courier_details;
  const currentIdx  = getLegacyPipelineIdx(job.current_status, job.status_history || []);
  const currentStep = LEGACY_PIPELINE[currentIdx] || LEGACY_PIPELINE[0];
  const statusColor = STATUS_DOT[job.current_status] || DEFAULT_DOT;
  const addrParts   = [cust.address, cust.landmark ? `Near: ${cust.landmark}` : null, cust.city, cust.state, cust.pincode].filter(Boolean);

  return (
    <div style={{
      width:"148mm", background:"#ffffff",
      borderRadius:"12px", overflow:"hidden",
      boxShadow:"0 4px 24px rgba(0,0,0,0.10)",
      fontFamily:"'Inter', -apple-system, sans-serif",
    }}>
      {/* Green header */}
      <div style={{ background:"#16a34a", padding:"14px 16px 12px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
            <div style={{ background:"white", borderRadius:"8px", padding:"4px 8px" }}>
              <img src="/logo.jpg" alt="logo" style={{ height:"28px", width:"auto", objectFit:"contain" }}/>
            </div>
            <div>
              <p style={{ fontSize:"13px", fontWeight:800, color:"white", margin:0, fontFamily:"'Space Grotesk',sans-serif" }}>{companyName}</p>
              <p style={{ fontSize:"9px", color:"rgba(255,255,255,0.8)", margin:0 }}>{tagline}</p>
            </div>
          </div>
          <div style={{ textAlign:"right" }}>
            <p style={{ fontSize:"18px", fontFamily:"monospace", fontWeight:800, color:"white", margin:0 }}>{job.sc_no}</p>
            <p style={{ fontSize:"9px", color:"rgba(255,255,255,0.75)", margin:"2px 0 0" }}>Received: {fmtDate(job.created_at)}</p>
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div style={{ background: statusColor, padding:"6px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <p style={{ fontSize:"11px", fontWeight:700, color:"white", margin:0 }}>STATUS: {job.current_status?.toUpperCase()}</p>
        <p style={{ fontSize:"9px", color:"rgba(255,255,255,0.85)", margin:0 }}>Updated: {fmtDate(job.updated_at)}</p>
      </div>

      {/* Body */}
      <div style={{ padding:"14px 16px" }}>

        {/* Customer */}
        <div style={{ border:"1px solid #e2e8f0", borderRadius:"8px", padding:"10px", marginBottom:"10px" }}>
          <p style={{ fontSize:"9px", fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 6px" }}>👤 Customer</p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 12px" }}>
            <div>
              <p style={{ fontSize:"12px", fontWeight:700, color:"#1e293b", margin:"0 0 2px" }}>{cust.name || "—"}</p>
              <p style={{ fontSize:"10px", color:"#374151", margin:"0 0 1px" }}>📞 {cust.phone || "—"}</p>
              {cust.alt_phone && <p style={{ fontSize:"10px", color:"#64748b", margin:0 }}>Alt: {cust.alt_phone}</p>}
            </div>
            {addrParts.length > 0 && (
              <div>
                <p style={{ fontSize:"8px", color:"#94a3b8", margin:"0 0 2px" }}>ADDRESS</p>
                <p style={{ fontSize:"9px", color:"#374151", margin:0, lineHeight:1.4 }}>{addrParts.join(", ")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Chargers */}
        {chargers.length > 0 && (
          <div style={{ border:"1px solid #e2e8f0", borderRadius:"8px", padding:"10px", marginBottom:"10px" }}>
            <p style={{ fontSize:"9px", fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 6px" }}>
              ⚡ Charger Details ({chargers.length} unit{chargers.length !== 1 ? "s" : ""})
            </p>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ borderBottom:"1px solid #f1f5f9" }}>
                  {["#","Type","Model","Serial No.","Body","Warranty","Mfg"].map(h => (
                    <th key={h} style={{ textAlign:"left", fontSize:"7.5px", fontWeight:700, color:"#94a3b8", textTransform:"uppercase", padding:"2px 5px", letterSpacing:"0.04em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chargers.map((c, i) => {
                  const mfg = [c.mfg_month ? MONTHS_SHORT[parseInt(c.mfg_month)-1] : null, c.mfg_year].filter(Boolean).join(" ");
                  const wc  = WARRANTY_BADGE[c.warranty_status];
                  return (
                    <tr key={i} style={{ borderBottom: i < chargers.length - 1 ? "1px solid #f8fafc" : "none" }}>
                      <td style={{ padding:"3px 5px", fontSize:"9px", color:"#94a3b8", fontWeight:600 }}>{i+1}</td>
                      <td style={{ padding:"3px 5px", fontSize:"9px", color:"#374151", whiteSpace:"nowrap" }}>
                        {CHARGER_ICONS[c.charger_type] || "⚡"} {c.charger_type || "—"}
                      </td>
                      <td style={{ padding:"3px 5px", fontSize:"10px", fontFamily:"monospace", fontWeight:700, color:"#16a34a", whiteSpace:"nowrap" }}>{c.charger_spec || "—"}</td>
                      <td style={{ padding:"3px 5px", fontSize:"9px", fontFamily:"monospace", color:"#1e293b" }}>{c.serial_number || "—"}</td>
                      <td style={{ padding:"3px 5px", fontSize:"9px", color:"#374151" }}>{c.body_type || "—"}</td>
                      <td style={{ padding:"3px 5px" }}>
                        {c.warranty_status ? (
                          <span style={{ fontSize:"8px", fontWeight:700, padding:"1px 5px", borderRadius:"10px", background:wc?.bg||"#f8fafc", color:wc?.text||"#475569", border:`1px solid ${wc?.border||"#e2e8f0"}`, whiteSpace:"nowrap" }}>
                            {c.warranty_status}
                          </span>
                        ) : "—"}
                      </td>
                      <td style={{ padding:"3px 5px", fontSize:"9px", color:"#94a3b8", whiteSpace:"nowrap" }}>{mfg || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Fault */}
        {specs.fault_description && (
          <div style={{ border:"1px solid #fde68a", background:"#fffbeb", borderRadius:"8px", padding:"8px 10px", marginBottom:"10px" }}>
            <p style={{ fontSize:"8px", fontWeight:700, color:"#92400e", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 3px" }}>🔧 Fault / Problem</p>
            <p style={{ fontSize:"9px", color:"#1e293b", margin:0, lineHeight:1.4 }}>
              {specs.fault_description.slice(0,160)}{specs.fault_description.length > 160 ? "…" : ""}
            </p>
          </div>
        )}

        {/* Courier */}
        {courier?.awb_number && (
          <div style={{ border:"1px solid #bfdbfe", background:"#eff6ff", borderRadius:"8px", padding:"8px 10px", marginBottom:"10px" }}>
            <p style={{ fontSize:"8px", fontWeight:700, color:"#1e40af", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 4px" }}>🚚 Courier / Dispatch</p>
            <div style={{ display:"flex", gap:"24px" }}>
              {courier.courier_name && (
                <div>
                  <p style={{ fontSize:"8px", color:"#3b82f6", margin:"0 0 1px" }}>Courier</p>
                  <p style={{ fontSize:"10px", fontWeight:700, color:"#1e293b", margin:0 }}>{courier.courier_name}</p>
                </div>
              )}
              <div>
                <p style={{ fontSize:"8px", color:"#3b82f6", margin:"0 0 1px" }}>AWB / Tracking No.</p>
                <p style={{ fontSize:"10px", fontFamily:"monospace", fontWeight:700, color:"#1d4ed8", margin:0 }}>{courier.awb_number}</p>
              </div>
            </div>
          </div>
        )}

        {/* Service Journey */}
        <div style={{ border:"1px solid #e2e8f0", borderRadius:"8px", padding:"10px", marginBottom:"10px" }}>
          <p style={{ fontSize:"9px", fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.05em", margin:"0 0 6px" }}>
            🗺️ Service Journey — Step {currentIdx+1} of {LEGACY_PIPELINE.length}
          </p>
          <div style={{ height:"4px", background:"#f1f5f9", borderRadius:"4px", overflow:"hidden", marginBottom:"6px" }}>
            <div style={{ height:"100%", background:"#16a34a", borderRadius:"4px", width:`${((currentIdx+1)/LEGACY_PIPELINE.length*100).toFixed(0)}%` }}/>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(9,1fr)", gap:"2px", textAlign:"center" }}>
            {LEGACY_PIPELINE.map((step, i) => {
              const done = i < currentIdx, current = i === currentIdx;
              const dot  = STATUS_DOT[step.status] || DEFAULT_DOT;
              return (
                <div key={`${step.status}-${i}`} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"2px" }}>
                  <div style={{ width:"16px", height:"16px", borderRadius:"50%", background: current ? dot : done ? "#16a34a" : "#e2e8f0", display:"flex", alignItems:"center", justifyContent:"center", border: current ? `2px solid ${dot}` : "none", flexShrink:0 }}>
                    {current ? <span style={{ fontSize:"8px" }}>{step.emoji}</span> : done ? <span style={{ color:"white", fontSize:"7px", fontWeight:700 }}>✓</span> : null}
                  </div>
                  <p style={{ fontSize:"6px", lineHeight:1.2, margin:0, color: current ? dot : done ? "#15803d" : "#cbd5e1", fontWeight: current ? 700 : done ? 600 : 400, maxWidth:"20px", textAlign:"center", wordBreak:"break-word" }}>
                    {step.status.split(" ").slice(0,2).join(" ")}
                  </p>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop:"6px", padding:"4px 8px", borderRadius:"6px", background:`${statusColor}18`, border:`1px solid ${statusColor}40` }}>
            <p style={{ fontSize:"9px", fontWeight:700, margin:0, color: statusColor }}>{currentStep.emoji} {job.current_status}</p>
            {(job.status_history||[]).slice(-2).reverse().map((h, i) => (
              <p key={i} style={{ fontSize:"7px", color:"#94a3b8", margin:"1px 0 0" }}>{h.status} — {fmtDT(h.timestamp)}</p>
            ))}
          </div>
        </div>

        {/* Barcode + QR */}
        <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"10px" }}>
          <div style={{ flex:1, border:"1px solid #e2e8f0", borderRadius:"8px", padding:"6px 8px", textAlign:"center" }}>
            <p style={{ fontSize:"8px", color:"#94a3b8", margin:"0 0 3px" }}>SCAN BARCODE</p>
            <Barcode value={job.sc_no} width={1.3} height={30} fontSize={9} margin={2} background="#ffffff" displayValue={true}/>
          </div>
          <div style={{ border:"1px solid #bbf7d0", background:"#f0fdf4", borderRadius:"8px", padding:"8px", textAlign:"center", flexShrink:0 }}>
            <p style={{ fontSize:"7px", color:"#15803d", fontWeight:600, margin:"0 0 4px" }}>TRACK ONLINE</p>
            <QRCodeSVG value={trackingUrl} size={60} level="M" includeMargin={false}/>
            <p style={{ fontSize:"6.5px", color:"#94a3b8", margin:"3px 0 0", wordBreak:"break-all", maxWidth:"72px", lineHeight:1.2 }}>{trackingUrl}</p>
          </div>
        </div>

        {/* Footer */}
        <div style={{ borderTop:"1px solid #e2e8f0", paddingTop:"7px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            {sc?.phone && <p style={{ fontSize:"9px", color:"#64748b", margin:"0 0 1px" }}>📞 {sc.phone}{sc.phone2 ? ` / ${sc.phone2}` : ""}</p>}
            {sc?.email && <p style={{ fontSize:"9px", color:"#64748b", margin:0 }}>✉️ {sc.email}</p>}
          </div>
          <div style={{ textAlign:"right" }}>
            <p style={{ fontSize:"8px", color:"#94a3b8", margin:"0 0 1px" }}>SC Job: <strong style={{ fontFamily:"monospace" }}>{job.sc_no}</strong></p>
            <p style={{ fontSize:"8px", color:"#94a3b8", margin:0 }}>Printed: {fmtDate(new Date().toISOString())}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Bulk Print Page ─────────────────────────────────────────────────────
export default function SCBulkPrint() {
  const [jobs,    setJobs]    = useState([]);
  const [sc,      setSC]      = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ids    = params.get("jobs")?.split(",").filter(Boolean) || [];
    if (!ids.length) { setError("No SC jobs selected"); setLoading(false); return; }

    Promise.all([
      axios.get(`${API}/settings`).then(r => setSC(r.data)).catch(() => {}),
      Promise.all(
        ids.map(id => api.get(`${API}/sc/jobs/${id}`).then(r => r.data).catch(() => null))
      ).then(results => setJobs(results.filter(Boolean))),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh" }}>
      <div style={{ width:"36px", height:"36px", borderRadius:"50%", border:"3px solid #16a34a", borderTopColor:"transparent", animation:"spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );

  if (error || !jobs.length) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"100vh", gap:"12px" }}>
      <p style={{ color:"#64748b", fontSize:"14px" }}>{error || "No SC jobs found"}</p>
      <button onClick={() => window.history.back()}
        style={{ fontSize:"13px", color:"#64748b", padding:"8px 16px", border:"1px solid #e2e8f0", borderRadius:"8px", background:"white", cursor:"pointer" }}>
        ← Back
      </button>
    </div>
  );

  const companyName = sc?.name || "XSTRONG POWER EV CHARGER";

  return (
    <>
      {/* Print toolbar */}
      <div className="no-print" style={{
        position:"fixed", top:0, left:0, right:0, zIndex:50,
        background:"white", borderBottom:"1px solid #e2e8f0",
        padding:"10px 20px", display:"flex", alignItems:"center", justifyContent:"space-between",
        boxShadow:"0 1px 4px rgba(0,0,0,0.08)"
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <img src="/logo.jpg" alt="logo" style={{ height:"34px", width:"auto", objectFit:"contain" }}/>
          <div>
            <p style={{ margin:0, fontSize:"13px", fontWeight:700, color:"#0f172a", fontFamily:"'Space Grotesk',sans-serif" }}>
              SC Bulk Print — {jobs.length} Job Card{jobs.length !== 1 ? "s" : ""}
            </p>
            <p style={{ margin:0, fontSize:"11px", color:"#94a3b8" }}>{companyName} · One A5 card per page</p>
          </div>
        </div>
        <div style={{ display:"flex", gap:"8px" }}>
          <button onClick={() => window.history.back()}
            style={{ fontSize:"13px", color:"#64748b", padding:"6px 14px", border:"1px solid #e2e8f0", borderRadius:"8px", background:"white", cursor:"pointer" }}>
            ← Back
          </button>
          <button onClick={() => window.print()}
            style={{ fontSize:"13px", fontWeight:700, color:"white", background:"#16a34a", padding:"6px 18px", border:"none", borderRadius:"10px", cursor:"pointer" }}
            data-testid="sc-bulk-print-btn">
            🖨️ Print All ({jobs.length})
          </button>
        </div>
      </div>

      {/* Cards — one per page */}
      <div style={{ paddingTop:"64px", background:"#f0fdf4", minHeight:"100vh" }}>
        {jobs.map((job, idx) => (
          <div key={job.id} style={{
            display:"flex", justifyContent:"center",
            padding:"20px 16px",
            pageBreakAfter: idx < jobs.length - 1 ? "always" : "auto",
            breakAfter:     idx < jobs.length - 1 ? "page"   : "auto",
          }}>
            <SCJobCard job={job} sc={sc} />
          </div>
        ))}
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: A5 portrait; margin: 5mm; }
          .no-print { display: none !important; }
          body { background: white !important; padding: 0 !important; margin: 0 !important; }
          div[style*="paddingTop"] { padding-top: 0 !important; background: white !important; }
        }
      `}</style>
    </>
  );
}
